import { SHOP_CONTENT } from '@/features/storefront/content/shop';
import {
  SHOP_NAMESPACE,
  manifestDefaults,
  type ContentManifest,
} from '@/features/storefront/content/types';

/**
 * Every theme's editable copy, discovered by folder.
 *
 * The third glob in this directory, and deliberately the same shape as the
 * other two: a theme declares its words in `previewThemes/<id>/content.ts` and
 * the customiser offers them - no list in this file, no registration step, no
 * switch anywhere gains an arm. A theme that ships no `content.ts` has no copy
 * of its own to edit, which is true of every theme but Hyper today and is a
 * statement about that theme rather than a gap in this one.
 *
 * Eager, and safe to be: these modules carry strings and no React, so the
 * whole set costs a few kilobytes and the customiser can build an editor for
 * a theme the merchant has not applied yet.
 */
const modules = import.meta.glob<{ content: ContentManifest }>('./*/content.ts', { eager: true });

function folderId(path: string): string {
  // './hyper/content.ts' -> 'hyper'
  return path.split('/')[1];
}

/**
 * Each theme's manifest, namespaced by its folder.
 *
 * The namespace is taken from the folder rather than from what the module
 * declared, for the same reason a theme's id is: a theme cannot claim a
 * namespace that disagrees with where it lives, and two themes cannot collide
 * on one by copy-paste. A theme's own `namespace` field is therefore
 * documentation, and this is the enforcement.
 */
export const THEME_CONTENT: Record<string, ContentManifest> = Object.fromEntries(
  Object.entries(modules).map(([path, module]) => {
    const id = folderId(path);
    return [id, { namespace: id, groups: module.content.groups }];
  }),
);

/**
 * Every default in the build, by full key.
 *
 * One flat map rather than a lookup per theme, because that is exactly what a
 * rendering component needs: it holds a key and wants the words, and which
 * theme wrote them is already settled by the key's namespace. Keys cannot
 * collide across themes - the namespace is the folder - and a test pins that
 * they do not collide within one either.
 */
export const CONTENT_DEFAULTS: Record<string, string> = {
  ...manifestDefaults(SHOP_CONTENT),
  ...Object.values(THEME_CONTENT).reduce<Record<string, string>>(
    (all, manifest) => ({ ...all, ...manifestDefaults(manifest) }),
    {},
  ),
};

/** One theme's manifest, or nothing if it has no copy of its own. */
export function themeContent(id: string): ContentManifest | undefined {
  return THEME_CONTENT[id];
}

/**
 * The manifests a merchant is offered for a shop wearing these themes.
 *
 * The shop's own copy always, then each theme that actually draws some part of
 * the page - which is usually one, and is more than one for a merchant who has
 * pinned their header or their product page to a different theme than the rest
 * of the shop. Ordered so the shared copy comes first and de-duplicated so a
 * theme worn in three areas is offered once.
 */
export function contentManifests(themeIds: readonly string[]): ContentManifest[] {
  const seen = new Set<string>([SHOP_NAMESPACE]);
  const manifests: ContentManifest[] = [SHOP_CONTENT];

  for (const id of themeIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const manifest = themeContent(id);
    if (manifest) manifests.push(manifest);
  }

  return manifests;
}
